import React, { useContext } from 'react';
import classNames from 'classnames';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import EDPanel from '../../../../../BaseComponents/Panel/EDPanel';
import EDIcon from '../../../../../BaseComponents/Icon/EDIcon';
import EDTypography from '../../../../../BaseComponents/Typography/EDTypography';
import EDDivider from '../../../../../BaseComponents/Divider/EDDivider';
import EDIconButton from '../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import { ActiveDatabaseContext } from '../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';

const styles = (theme: Theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
  },
  accountValueGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  topAccountValueGroup: {
    marginBottom: theme.spacing(2),
  },

  row: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  topRow: {
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(1),
  },
  valueText: {
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(1),
  },
  topValueText: {
    color: theme.customProperties.AccountCard.topValueTextColor,
  },
  lowerValueText: {
    fontWeight: theme.customProperties.AccountCard.lowerValueFontWeight,
  },
});

interface IProps {
  classes?: any;
}

// For fake data for GAS demo
interface IAccountData {
  title: {
    accountText: string;
    balanceText: string;
    account: string;
    balance: string;
  };
  row1: { text: string; account: string; balance: string };
  row2: { text: string; account: string; balance: string };
}

const almstenaAccountData: IAccountData = {
  title: {
    accountText: 'Konto',
    balanceText: 'Saldo',
    account: '1930',
    balance: '1 572 436,00',
  },
  row1: {
    text: 'Förfallna inbetalningar kommande veckan',
    account: '1510',
    balance: '56 089,50',
  },
  row2: {
    text: 'Planerade utbetalningar kommande veckan',
    account: '2440',
    balance: '-287 653,00',
  },
};

const bjorkstenaAccountData: IAccountData = {
  title: {
    accountText: 'Konto',
    balanceText: 'Saldo',
    account: '1930',
    balance: '1 231 463,00',
  },
  row1: {
    text: 'Förfallna inbetalningar kommande veckan',
    account: '1510',
    balance: '46 024,50',
  },
  row2: {
    text: 'Planerade utbetalningar kommande veckan',
    account: '2440',
    balance: '-317 325,00',
  },
};

const bokstenaAccountData: IAccountData = {
  title: {
    accountText: 'Konto',
    balanceText: 'Saldo',
    account: '1930',
    balance: '1 550 232,00',
  },
  row1: {
    text: 'Förfallna inbetalningar kommande veckan',
    account: '1510',
    balance: '38 212,50',
  },
  row2: {
    text: 'Planerade utbetalningar kommande veckan',
    account: '2440',
    balance: '-352 426,00',
  },
};

const karlsbyAccountData: IAccountData = {
  title: {
    accountText: 'Konto',
    balanceText: 'Saldo',
    account: '1930',
    balance: '1 620 151,00',
  },
  row1: {
    text: 'Förfallna inbetalningar kommande veckan',
    account: '1510',
    balance: '42 151,50',
  },
  row2: {
    text: 'Planerade utbetalningar kommande veckan',
    account: '2440',
    balance: '-272 126,00',
  },
};

const nystadAccountData: IAccountData = {
  title: {
    accountText: 'Konto',
    balanceText: 'Saldo',
    account: '1930',
    balance: '2 121 213,00',
  },
  row1: {
    text: 'Förfallna inbetalningar kommande veckan',
    account: '1510',
    balance: '62 011,50',
  },
  row2: {
    text: 'Planerade utbetalningar kommande veckan',
    account: '2440',
    balance: '-212 126,00',
  },
};

const pilstenaAccountData: IAccountData = {
  title: {
    accountText: 'Konto',
    balanceText: 'Saldo',
    account: '1930',
    balance: '2 416 883,00',
  },
  row1: {
    text: 'Förfallna inbetalningar kommande veckan',
    account: '1510',
    balance: '61 423,50',
  },
  row2: {
    text: 'Planerade utbetalningar kommande veckan',
    account: '2440',
    balance: '-304 152,00',
  },
};

const defaultAccountData: IAccountData = {
  title: {
    accountText: 'Konto',
    balanceText: 'Saldo',
    account: '1930',
    balance: '1 614 143,00',
  },
  row1: {
    text: 'Förfallna inbetalningar kommande veckan',
    account: '1510',
    balance: '61 201,00',
  },
  row2: {
    text: 'Planerade utbetalningar kommande veckan',
    account: '2440',
    balance: '-311 222,00',
  },
};

const AccountStatus: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const { classes } = props;
  const activeDatabaseContext = useContext(ActiveDatabaseContext);
  const activeDatabase = activeDatabaseContext.activeDatabase;

  let accountData = defaultAccountData;

  if (activeDatabase === 'Almstena församling') {
    accountData = almstenaAccountData;
  } else if (activeDatabase === 'Björkstena pastorat') {
    accountData = bjorkstenaAccountData;
  } else if (activeDatabase === 'Bokstena församling') {
    accountData = bokstenaAccountData;
  } else if (activeDatabase === 'Karlsby församling') {
    accountData = karlsbyAccountData;
  } else if (activeDatabase === 'Nystad pastorat') {
    accountData = nystadAccountData;
  } else if (activeDatabase === 'Pilstena församling') {
    accountData = pilstenaAccountData;
  }

  return (
    <EDPanel
      title="Kontostatus"
      avatar={<EDIcon color="action" iconType="bar_chart" size="large" />}
      headerPadding="normal"
      flex
      titleAction={
        <EDIconButton
          tooltip=""
          onClick={() => {}}
          icon="more_vert"
          margin="none"
        />
      }
      minWidth={350}
    >
      <div className={classes.content}>
        <div
          className={`${classes.accountValueGroup} ${classes.topAccountValueGroup}`}
        >
          <div className={classes.row}>
            <EDTypography bold className={classes.topRow}>
              {accountData.title.accountText}
            </EDTypography>
            <EDTypography bold className={classes.topRow}>
              {accountData.title.balanceText}
            </EDTypography>
          </div>
          <EDDivider />
          <div className={classes.row}>
            <EDTypography
              variant="h6"
              color="primary"
              className={classNames(classes.valueText, classes.topValueText)}
            >
              {accountData.title.account}
            </EDTypography>
            <EDTypography
              variant="subtitle1"
              color="primary"
              className={classNames(classes.valueText, classes.topValueText)}
            >
              {accountData.title.balance}
            </EDTypography>
          </div>
        </div>
        <div className={classes.accountValueGroup}>
          <div className={classes.row}>
            <EDTypography>{accountData.row1.text}</EDTypography>
          </div>
          <EDDivider marginBottom={0.5} />
          <div className={classes.row}>
            <EDTypography
              color="textSecondary"
              className={classNames(classes.valueText, classes.lowerValueText)}
            >
              {accountData.row1.account}
            </EDTypography>
            <EDTypography
              color="textSecondary"
              className={classNames(classes.valueText, classes.lowerValueText)}
            >
              {accountData.row1.balance}
            </EDTypography>
          </div>
          <EDDivider marginBottom={0.5} />
        </div>
        <div className={classes.accountValueGroup}>
          <div className={classes.row}>
            <EDTypography>{accountData.row2.text}</EDTypography>
          </div>
          <EDDivider marginBottom={0.5} />
          <div className={classes.row}>
            <EDTypography
              color="textSecondary"
              className={classNames(classes.valueText, classes.lowerValueText)}
            >
              {accountData.row2.account}
            </EDTypography>
            <EDTypography
              color="textSecondary"
              className={classNames(classes.valueText, classes.lowerValueText)}
            >
              {accountData.row2.balance}
            </EDTypography>
          </div>
          <EDDivider marginBottom={0.5} />
        </div>
      </div>
    </EDPanel>
  );
};

export default withStyles(AccountStatus, styles);
